import { useEffect, useMemo, useState } from "react";

import TableFirmSettings from "../common/table-firm-settings";
import { useGetFirmUserTypes } from "../user-roles/hooks/useGetRolesPageApi";
import HintModal from "../common/hint-modal";
import TabsClient from "../../../Components/new-client-dashboard/Tabs";

import useGetCaseTypeRoles from "./hooks/useGetCaseTypeRoles";
import CaseWorkerRoleModal from "./Modals/edit-case-type-roles";
import useSaveCaseTypeRoles from "./hooks/useSaveCaseTypeRoles";

const COLLAPSE_BREAKPOINT = 1080;

const useIsCollapsed = () => {
  const [isCollapsed, setIsCollapsed] = useState(
    () => window.matchMedia(`(max-width: ${COLLAPSE_BREAKPOINT}px)`).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${COLLAPSE_BREAKPOINT}px)`);
    const handler = (e) => setIsCollapsed(e.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  return isCollapsed;
};

const CaseTypeRoles = () => {
  const points = [
    "Firm settings panel instruction point one",
    "Firm settings panel instruction point two",
    "Firm settings panel instruction point three",
  ];
  const [selectedCaseType, setSelectedCaseType] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [caseWorkerRoles, setCaseWorkerRoles] = useState([]);
  const [data, setData] = useState([]); // Stores fetched case types
  const [selectedCategory, setSelectedCategory] = useState("all"); // Track selected category, default to 'all'
  const { saveCaseTypeRoles, loading: _saveLoading, error: _saveError } = useSaveCaseTypeRoles();
  const { data: caseTypeRolesData, _error, _loading, refetch } = useGetCaseTypeRoles();
  const {
    data: firmUserTypes,
    loading: _firmUserTypeLoading,
    error: _firmUserTypeError,
  } = useGetFirmUserTypes();
  const isCollapsed = useIsCollapsed();

  const [showHints, setShowHints] = useState(false);

  const handleHintToggle = () => {
    setShowHints(!showHints);
  };

  useEffect(() => {
    if (firmUserTypes) {
      setCaseWorkerRoles(firmUserTypes); // Populate roles from firm user types
    }
  }, [firmUserTypes]);

  useEffect(() => {
    if (caseTypeRolesData) {
      setData(caseTypeRolesData); // Store fetched case type roles data
    }
  }, [caseTypeRolesData]);

  // Extract unique categories from data
  const categories = useMemo(() => {
    if (!data || data.length === 0) return [];

    const uniqueCategories = [
      ...new Map(
        data
          .filter((item) => item.for_case_type?.case_type_category)
          .map((item) => [
            item.for_case_type.case_type_category.id,
            item.for_case_type.case_type_category,
          ])
      ).values(),
    ];

    return uniqueCategories;
  }, [data]);

  // Create tabs from categories with "All" as the first tab
  const categoryTabs = useMemo(
    () => [
      {
        id: "all",
        label: "All",
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-60)",
        activeColor: "white",
        onClick: () => setSelectedCategory("all"),
        leftHand: false,
      },
      ...categories.map((category) => ({
        id: category.id,
        label: category.name,
        className: "tab-trapezium-cycles-corners",
        background: "var(--primary-60)",
        activeColor: "white",
        onClick: () => setSelectedCategory(category.id),
        leftHand: false,
      })),
    ],
    [categories]
  );

  // Filter data based on selected category
  const filteredData = useMemo(() => {
    if (!data) return [];
    if (selectedCategory === "all") return data;
    return data.filter(
      (caseType) => caseType.for_case_type?.case_type_category?.id === selectedCategory
    );
  }, [data, selectedCategory]);

  const handleEditClick = (caseType) => {
    setSelectedCaseType(caseType);
    setShowEditModal(true);
  };

  const handleRoleChange = (updatedRoles) => {
    // Update selected case type roles dynamically
    setSelectedCaseType((prev) => ({
      ...prev,
      user_case_type_1: updatedRoles[0],
      user_case_type_2: updatedRoles[1],
      user_case_type_3: updatedRoles[2],
      user_case_type_4: updatedRoles[3],
      user_case_type_5: updatedRoles[4],
      user_case_type_6: updatedRoles[5],
    }));
  };

  const handleSaveRoles = async (updatedData) => {
    const payload = {
      id: updatedData.id,
      user_case_type_1:
        updatedData.user_case_type_1.id !== 0 ? updatedData.user_case_type_1.id : undefined,
      user_case_type_2:
        updatedData.user_case_type_2.id !== 0 ? updatedData.user_case_type_2.id : undefined,
      user_case_type_3:
        updatedData.user_case_type_3.id !== 0 ? updatedData.user_case_type_3.id : undefined,
      user_case_type_4:
        updatedData.user_case_type_4.id !== 0 ? updatedData.user_case_type_4.id : undefined,
      user_case_type_5:
        updatedData.user_case_type_5.id !== 0 ? updatedData.user_case_type_5.id : undefined,
      user_case_type_6:
        updatedData.user_case_type_6.id !== 0 ? updatedData.user_case_type_6.id : undefined,
    };

    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        payload[key] = "";
      }
    });
    await saveCaseTypeRoles(payload);
    refetch();
    setShowEditModal(false);
  };

  return (
    <div className="tab-pane fade firm-settings-user-perms-fs active show">
      <div className="d-flex">
        <div className="w-100">
          <TabsClient
            currentTab={selectedCategory}
            tabsData={categoryTabs}
            height={25}
            labelClass=" justify-content-start"
          />
        </div>
        <div
          className="d-flex justify-content-end"
          onClick={() => {
            handleHintToggle();
          }}
        >
          <i className="ic ic-19 ic-help-2 cursor-pointer" />
        </div>
      </div>
      <TableFirmSettings>
        <thead>
          <tr id="tb-header">
            <th style={{ width: "25px", minWidth: "25px", maxWidth: "25px" }} />
            <th className="td-autosize text-center">Category</th>
            <th />
            <th className="td-autosize text-center">Case Type</th>
            <th />
            <th className="text-center">Default Roles</th>
            <th />
            <th className="td-autosize text-center" />
            <th style={{ width: "10px", minWidth: "10px", maxWidth: "10px" }} />
          </tr>
        </thead>
        <tbody>
          {filteredData &&
            filteredData?.map((caseType, index) => (
              <tr key={caseType.id} className="">
                <td
                  style={{
                    color: "var(--primary-25)",
                    fontWeight: "600",
                  }}
                >
                  {index + 1}
                </td>
                <td className="text-start">{caseType.for_case_type.case_type_category?.name}</td>
                <td />
                <td>
                  <span
                    className="d-flex align-items-center justify-content-start"
                    style={{
                      gap: "5px",
                    }}
                  >
                    {caseType?.for_case_type?.casetype_icon && (
                      <img
                        src={caseType?.for_case_type?.casetype_icon}
                        className="ic-15 d-flex align-items-center justify-content-center"
                      />
                    )}
                    {caseType.for_case_type.name}
                  </span>
                </td>
                <td />

                <td className="td-autosize text-center white-space-inherit">
                  <table className="m-t-5 m-b-5">
                    <tbody>
                      {!isCollapsed &&
                        Array.from({ length: 3 }).map((_, index) => (
                          <tr
                            key={index}
                            className="transparent-background"
                            id="case-type-role"
                            style={{ height: "25px" }}
                          >
                            <>
                              <td className="transparent-background border-0 case-worker-role">{`Role ${index + 1}`}</td>
                              <td className="transparent-background border-0 d-flex justify-content-center align-items-center">
                                <span
                                  className="ms-1 me-1 padding-5 height-21 d-flex align-items-center"
                                  style={{
                                    width: "fit-content",
                                    background: "var(--primary)",
                                    color: "#fff",
                                    fontWeight: "600",
                                    padding: "0 5px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  {caseType[`user_case_type_${index + 1}`]?.name}
                                </span>
                              </td>
                              <td className="transparent-background border-0 case-worker-role ms-1">{`Role ${index + 4}`}</td>
                              <td className="transparent-background border-0 d-flex justify-content-center align-items-center">
                                <span
                                  className="ms-1 padding-5 height-21 d-flex align-items-center"
                                  style={{
                                    width: "fit-content",
                                    background: "var(--primary)",
                                    color: "#fff",
                                    fontWeight: "600",
                                    padding: "0 5px",
                                    borderRadius: "3px",
                                  }}
                                >
                                  {caseType[`user_case_type_${index + 4}`]?.name}
                                </span>
                              </td>
                            </>
                          </tr>
                        ))}

                      {isCollapsed &&
                        Array.from({ length: 6 }).map((_, index) => (
                          <tr
                            key={index}
                            className="transparent-background m-b-5 "
                            id="case-type-role"
                            style={{ height: "25px" }}
                          >
                            <td className="transparent-background border-0 case-worker-role">{`Role ${index + 1}`}</td>
                            <td className="transparent-background border-0 d-flex justify-content-center align-items-center">
                              <span
                                className="badge text-bg-primary m-l-5 padding-5 height-21 d-flex align-items-center"
                                style={{ width: "fit-content" }}
                              >
                                {caseType[`user_case_type_${index + 1}`]?.name}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </td>
                <td />
                <td className="" style={{ height: "auto", marginRight: "10px" }}>
                  <button
                    className="btn btn-secondary height-25 d-flex justify-content-center align-items-center btn-hover-green"
                    onClick={() => handleEditClick(caseType)}
                  >
                    Edit
                  </button>
                </td>
                <td />
              </tr>
            ))}
        </tbody>
      </TableFirmSettings>
      {showEditModal && (
        <CaseWorkerRoleModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          caseType={selectedCaseType}
          caseWorkerRoles={caseWorkerRoles}
          onRoleChange={handleRoleChange}
          handleSave={handleSaveRoles}
          modalTitle="Select the Case Worker Roles for Case Workers 1-6 on each Case Type in"
          saveButtonText="Save"
        />
      )}
      {showHints && <HintModal show={showHints} handleClose={handleHintToggle} points={points} />}
    </div>
  );
};

export default CaseTypeRoles;
